import {
  alpha,
  createTheme,
  type Palette,
  listItemIconClasses,
  stepConnectorClasses,
  switchClasses,
  chipClasses,
  type Theme,
  type Components,
} from '@mui/material'
// eslint-disable-next-line import-x/no-empty-named-blocks
import type {} from '@mui/x-data-grid/themeAugmentation'
import { deepmerge } from '@mui/utils'
import {
  CheckboxChecked,
  CheckboxIndeterminate,
  CheckboxUnchecked,
  CircleError,
  CheckCircle,
  Warning,
  Info,
} from '@dhl/icons'
import { breakpoints } from './breakpoints.js'
import { type DhlThemeOptions } from './types.js'

export const shape = {
  borderRadius: 4,
}

const lightModeComponents = (
  palette: Palette,
  hasResponsiveTypography: boolean,
  typography: Theme['typography'],
  options: Omit<DhlThemeOptions, 'device'>
) => {
  const { components } = createTheme({
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            height: '100%',
            colorScheme: palette.mode,
          },
          body: {
            height: '100%',
            WebkitFontSmoothing: 'antialiased',
            fontSize: 14,
            userSelect: options.userSelect || 'inherit',
            ...(hasResponsiveTypography
              ? {
                  [breakpoints.up('sm')]: {
                    fontSize: '12.6px',
                  },
                }
              : {}),
            '@media print': {
              height: 'auto',
            },
          },
          '#root': {
            height: '100%',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          colorPrimary: {
            backgroundColor: 'transparent',
          },
          root: {
            border: 0,
            background: 'linear-gradient(90deg, #FFCC00 0%, #FFCC00 30%, #FFE57F 79%, #FFF0B2 100%)',
          },
        },
      },
      // No more ripple, on the whole application 💣!
      ...(options.disableRipple
        ? {
            MuiButtonBase: {
              defaultProps: {
                disableRipple: true,
              },
            },
          }
        : {}),
      MuiAutocomplete: {
        styleOverrides: {
          tagSizeMedium: {
            marginLeft: '8px',
            marginRight: 0,
            marginBottom: 0,
            marginTop: '18px',
            '&:first-of-type': {
              marginLeft: '14px',
            },
          },
          tagSizeSmall: {
            marginLeft: '8px',
            marginRight: 0,
            '&:first-of-type': {
              marginLeft: '14px',
            },
            [breakpoints.up('sm')]: {
              marginLeft: '8px',
              '&:first-of-type': {
                marginLeft: '8px',
              },
            },
          },
          root: {
            '& .MuiFilledInput-root': {
              padding: 0,
              '& .MuiAutocomplete-input': {
                height: '22px',
                padding: '22px 14px 8px',
                [breakpoints.up('sm')]: {
                  height: '20px',
                  padding: '18px 14px 6px',
                },
              },
            },
            '& .MuiFilledInput-root.MuiInputBase-sizeSmall': {
              padding: 0,
              '& .MuiAutocomplete-input': {
                height: '20px',
                padding: '8px 14px 8px',
                [breakpoints.up('sm')]: {
                  height: '20px',
                  padding: '4px 6px 4px',
                },
              },
            },
            '& .MuiOutlinedInput-root': {
              padding: 0,
              '& .MuiAutocomplete-input': {
                height: '22px',
                padding: '24px 16px 10px',
                [breakpoints.up('sm')]: {
                  height: '20px',
                  padding: '20px 16px 8px',
                },
              },
            },
            '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
              padding: 0,
              '& .MuiAutocomplete-input': {
                height: '20px',
                padding: '10px 16px 10px',
                [breakpoints.up('sm')]: {
                  height: '20px',
                  padding: '6px 8px 6px',
                },
              },
            },
          },
        },
      },
      MuiAlert: {
        defaultProps: {
          iconMapping: {
            error: <CircleError sx={{ width: 20, height: 20 }} />,
            success: <CheckCircle sx={{ width: 20, height: 20 }} />,
            warning: <Warning sx={{ width: 20, height: 20 }} />,
            info: <Info sx={{ width: 20, height: 20 }} />,
          },
        },
        variants: [
          {
            props: { variant: 'standard' },
            style: { border: 0 },
          },
          {
            props: { variant: 'filled' },
            style: { border: 0 },
          },
          {
            props: { variant: 'standard', severity: 'error' },
            style: { color: palette.error.dark },
          },
          {
            props: { variant: 'standard', severity: 'info' },
            style: { color: palette.info.dark },
          },
          {
            props: { variant: 'standard', severity: 'warning' },
            style: { color: palette.warning.dark },
          },
          {
            props: { variant: 'outlined', severity: 'error' },
            style: { color: palette.error.dark },
          },
          {
            props: { variant: 'outlined', severity: 'info' },
            style: { color: palette.info.dark },
          },
          {
            props: { variant: 'outlined', severity: 'warning' },
            style: { color: palette.warning.dark },
          },
        ],
      },
      MuiAvatar: {
        styleOverrides: {
          colorDefault: {
            fontSize: '0.75rem',
            width: '24px',
            height: '24px',
          },
        },
      },
      MuiBottomNavigation: {
        styleOverrides: {
          root: {
            backgroundColor: palette.grey[100],
            boxShadow: '0px -1px 5px rgba(0, 0, 0, 0.1)',
            height: '64px',
            width: '100vw',
            position: 'fixed',
            bottom: 0,
          },
        },
      },
      MuiBottomNavigationAction: {
        styleOverrides: {
          label: {
            ...typography.caption,
          },
        },
      },
      MuiCard: {
        variants: [
          {
            props: { variant: 'filled' },
            style: {
              backgroundColor: palette.grey[200],
              borderColor: palette.grey[200],
              borderRadius: shape.borderRadius * 2,
            },
          },
        ],
      },
      MuiButton: {
        variants: [
          { props: { variant: 'contained', color: 'black' }, style: { color: palette.common.white } },
          {
            props: { size: 'xs' },
            style: {
              height: '32px',
              [breakpoints.up('sm')]: {
                height: '24px',
                minWidth: '24px',
                paddingLeft: '11px',
                paddingRight: '11px',
                '&:hover': {
                  padding: '4px 10px',
                },
                '&:focus-visible': {
                  padding: '4px 10px',
                },
              },
            },
          },
        ],
        styleOverrides: {
          root: {
            ...typography.button,
            textTransform: 'none',
            boxShadow: 'none !important',
            height: '40px',
            minWidth: '40px',
            [breakpoints.up('sm')]: {
              height: '32px',
              minWidth: '32px',
              fontSize: '14.4px',
            },
            '> .MuiSvgIcon-root:first-of-type': {
              marginLeft: '-12px',
              marginRight: '-12px',
            },
            '&.Mui-disabled': {
              color: palette.grey[500],
            },
          },
          contained: {
            '&.Mui-disabled': {
              backgroundColor: palette.grey[300],
            },
          },
          textSecondary: {
            color: palette.common.black,
          },
          outlinedSecondary: {
            color: palette.common.black,
          },
          sizeLarge: {
            height: '48px',
            minWidth: '48px',
            [breakpoints.up('sm')]: {
              height: '40px',
              minWidth: '40px',
            },
          },
          sizeSmall: {
            height: '36px',
            minWidth: '36px',
            [breakpoints.up('sm')]: {
              minWidth: '28px',
              height: '28px',
            },
          },
          containedSizeLarge: {
            height: '48px',
          },
          containedSizeSmall: {
            fontSize: '14px',
            [breakpoints.up('sm')]: {
              fontSize: '12.6px',
            },
          },
          outlinedSizeSmall: {
            fontSize: '14px',
            [breakpoints.up('sm')]: {
              fontSize: '12.6px',
            },
            '&:hover': {
              padding: '2px 8px',
            },
            '&:focus-visible': {
              padding: '2px 8px',
            },
          },
          outlinedSizeMedium: {
            '&:hover': {
              padding: '4px 14px',
            },
            '&:focus-visible': {
              padding: '4px 14px',
            },
          },
          outlinedSizeLarge: {
            '&:hover': {
              padding: '6px 20px',
            },
            '&:focus-visible': {
              padding: '6px 20px',
            },
          },
          outlined: {
            '&:hover': {
              borderWidth: '2px',
            },
            '&:focus-visible': {
              borderWidth: '2px',
            },
          },
        },
      },
      MuiButtonGroup: {
        styleOverrides: {
          contained: {
            boxShadow: 'none',
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          inputMultiline: ({ theme }) => ({
            marginTop: theme.spacing(1.25),
            padding: '0 0 0 2px !important',
          }),
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          sizeSmall: {},
          root: {
            ...typography.body1,
            color: palette.grey[600],
            '&.MuiInputLabel-shrink': {
              color: palette.grey[500],
              '&.Mui-error': {
                color: palette.error.main,
              },
            },
            '&.Mui-disabled': {
              color: palette.grey[400],
            },
          },
          filled: {
            transform: 'translate(16px, 18px) scale(1)',
            [breakpoints.up('sm')]: {
              transform: 'translate(16px, 14px) scale(1)',
            },
            '&.MuiInputLabel-shrink': {
              transform: 'translate(16px, 8px) scale(0.75)',
            },
          },
          outlined: {
            transform: 'translate(16px, 18px) scale(1)',
            [breakpoints.up('sm')]: {
              transform: 'translate(16px, 14px) scale(1)',
            },
            '&.MuiInputLabel-shrink': {
              transform: 'translate(16px, 8px) scale(0.75)',
            },
          },
          formControl: {
            transform: 'translate(16px, 18px) scale(1)',
            [breakpoints.up('sm')]: {
              transform: 'translate(16px, 14px) scale(1)',
            },
            '&.MuiInputLabel-shrink': {
              transform: 'translate(16px, 8px) scale(0.75)',
            },
            '&.MuiInputLabel-sizeSmall': {
              transform: 'translate(16px, 8px) scale(1)',
              [breakpoints.up('sm')]: {
                transform: 'translate(16px, 6px) scale(1) !important',
              },
            },
            '&.MuiInputLabel-sizeSmall.MuiInputLabel-shrink': {
              display: 'none',
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            zIndex: 1,
          },
          asterisk: {
            color: palette.warning.main,
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            position: 'fixed',
            top: 0,
            zIndex: 1250,
            width: '100vw',
          },
        },
      },
      MuiList: {
        styleOverrides: {
          root: {
            borderRadius: shape.borderRadius,
          },
          padding: {
            paddingTop: 0,
            paddingBottom: 0,
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          divider: {
            backgroundClip: 'border-box',
          },
        },
      },
      MuiListButton: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.09)',
            },
            '&:first-of-type': {
              borderTopRightRadius: shape.borderRadius - 1,
              borderTopLeftRadius: shape.borderRadius - 1,
            },
            '&:last-of-type': {
              borderBottomRightRadius: shape.borderRadius - 1,
              borderBottomLeftRadius: shape.borderRadius - 1,
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            color: palette.grey[600],
            minWidth: '42px !important',
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          root: ({ theme }) => ({
            marginTop: `${theme.spacing(1)} !important`,
            marginBottom: `${theme.spacing(1)} !important`,
          }),
          primary: {
            ...typography.h5,
          },
          secondary: {
            ...typography.body1,
          },
          multiline: {
            marginTop: '6px !important',
            marginBottom: '6px !important',
          },
        },
      },
      MuiListSubheader: {
        styleOverrides: {
          root: {
            ...typography.h5,
          },
          gutters: ({ theme }) => ({
            padding: theme.spacing(1.5, 0, 1.5, 2),
          }),
        },
      },
      MuiMenu: {
        defaultProps: {
          elevation: 1,
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            [`&:hover .${listItemIconClasses.root}`]: {
              color: palette.grey[700],
            },
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          marginDense: {
            marginTop: 0,
            marginBottom: 0,
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            backgroundColor: palette.grey[100],
            border: `2px solid ${palette.grey[100]}`,
            borderRadius: shape.borderRadius,
            minHeight: '56px',
            [breakpoints.up('sm')]: {
              minHeight: '48px',
            },
            '&::before': {
              display: 'none',
            },
            '&::after': {
              display: 'none',
            },
            '&:hover': {
              backgroundColor: palette.grey[100],
            },
            '@media (hover: none)': {
              '&:hover': {
                backgroundColor: `${palette.grey[100]} !important`,
              },
            },
            '&.Mui-focused': {
              border: `2px solid ${palette.grey[700]}`,
              backgroundColor: palette.grey[100],
            },
            '&.Mui-disabled': {
              backgroundColor: palette.grey[100],
            },
          },
          input: {
            ...typography.body1,
            color: palette.grey[700],
            padding: '22px 14px 8px',
            height: '22px',
            [breakpoints.up('sm')]: {
              height: '20px',
              padding: '18px 14px 6px',
              fontSize: '14.4px',
            },
            '&:-webkit-autofill': {
              WebkitBackgroundClip: 'text',
            },
            '&:disabled': {
              color: palette.grey[400],
              WebkitTextFillColor: palette.grey[400],
            },
            '&::-webkit-input-placeholder': {
              opacity: 1,
              color: palette.grey[600],
            },
            '&::-moz-placeholder': {
              opacity: 1,
              color: palette.grey[600],
            },
            '&::-ms-input-placeholder': {
              opacity: 1,
              color: palette.grey[600],
            },
          },
          adornedEnd: {
            paddingRight: '14px',
          },
          adornedStart: {
            [breakpoints.up('sm')]: {
              paddingLeft: '6px',
            },
          },
          sizeSmall: {
            minHeight: '40px',
            [breakpoints.up('sm')]: {
              minHeight: '32px',
            },
          },
          // @ts-ignore inputMultiline, inputSizeSmall and inputAdornedStart are not in the type definition but we can
          // use them and they still seem to work ....
          inputMultiline: {
            padding: '0',
            [breakpoints.up('sm')]: {
              padding: '0',
            },
          },
          inputSizeSmall: {
            padding: '8px 14px 8px',
            height: '20px',
            [breakpoints.up('sm')]: {
              padding: '4px 6px 4px',
            },
          },
          inputAdornedStart: {
            paddingLeft: 0,
            [breakpoints.up('sm')]: {
              paddingLeft: 0,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            background: palette.grey[100],
            '& fieldset': {
              borderColor: `${palette.grey[300]} !important`,
              borderWidth: '1px !important',
            },
            '&.Mui-error fieldset': {
              borderColor: `${palette.error.main} !important`,
            },
            '&.Mui-focused fieldset': {
              borderColor: `${palette.grey[700]} !important`,
              borderWidth: '2px !important',
            },
            '&.Mui-error.Mui-focused fieldset': {
              borderColor: `${palette.error.main} !important`,
              borderWidth: '2px',
            },
          },
          input: {
            ...typography.body1,
            height: '22px',
            color: palette.grey[700],
            padding: '24px 16px 10px',
            [breakpoints.up('sm')]: {
              height: '20px',
              padding: '20px 16px 8px',
              fontSize: '14.4px',
            },
            '&:-webkit-autofill': {
              transition: 'background-color 99999999s ease-in-out 0s',
              WebkitBackgroundClip: 'text',
            },
            '&:disabled': {
              color: palette.grey[400],
              WebkitTextFillColor: palette.grey[400],
            },
            '&[type=hidden]': {
              display: 'none',
            },
            '&[type=hidden] + fieldset': {
              display: 'none',
            },
            '&::-webkit-input-placeholder': {
              opacity: 1,
              color: palette.grey[600],
            },
            '&::-moz-placeholder': {
              opacity: 1,
              color: palette.grey[600],
            },
            '&::-ms-input-placeholder': {
              opacity: 1,
              color: palette.grey[600],
            },
          },
          inputMultiline: {
            padding: '8px 0 0',
            [breakpoints.up('sm')]: {
              padding: '8px 0 0',
            },
          },
          notchedOutline: {
            top: 0,
            borderWidth: 0,
            borderRadius: shape.borderRadius,
            '& legend': {
              position: 'absolute',
              left: '-5000px',
            },
          },
          adornedEnd: {
            paddingRight: '16px',
          },
          adornedStart: {
            [breakpoints.up('sm')]: {
              paddingLeft: '8px',
            },
          },
          inputSizeSmall: {
            padding: '10px 16px 10px',
            height: '20px',
            [breakpoints.up('sm')]: {
              padding: '6px 8px 6px',
            },
          },
          inputAdornedStart: {
            paddingLeft: 0,
            [breakpoints.up('sm')]: {
              paddingLeft: 0,
            },
          },
          inputAdornedEnd: {
            paddingRight: 0,
            [breakpoints.up('sm')]: {
              paddingRight: 0,
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            '&:focus': {
              backgroundColor: 'transparent',
            },
          },
          filled: {
            minHeight: 'unset',
            height: '20px',
            maxHeight: '22px',
            lineHeight: '22px',
            [breakpoints.up('sm')]: {
              maxHeight: '20px',
              lineHeight: '20px',
            },
          },
          outlined: {
            minHeight: 'unset',
            height: '20px',
            maxHeight: '22px',
            lineHeight: '22px',
            [breakpoints.up('sm')]: {
              maxHeight: '20px',
              lineHeight: '20px',
            },
          },
          iconFilled: {
            right: '12px',
          },
          iconOutlined: {
            right: '14px',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: palette.grey[100],
            border: `1px solid ${palette.grey[300]}`,
            color: palette.grey[700],
            '& .MuiDataGrid-main.MuiDataGrid-main': {
              border: 'none',
            },
          },
          rounded: {
            borderRadius: shape.borderRadius,
          },
          elevation1: {
            boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.05)',
          },
          elevation4: {
            boxShadow: '0px 1px 5px rgba(0, 0, 0, 0.1)',
          },
          outlined: {
            border: `1px solid ${palette.grey[100]}`,
          },
        },
      },
      MuiSnackbarContent: {
        styleOverrides: {
          message: {
            ...typography.h5,
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          fontSizeSmall: {
            fontSize: '1rem',
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: palette.background.backdrop,
          },
          invisible: {
            backgroundColor: 'transparent',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            height: '64px',
            display: 'flex',
            alignItems: 'center',
            alignSelf: 'center',
            paddingLeft: '16px',
            textTransform: 'initial',
            color: 'initial',
            ...typography.h5,
            [breakpoints.up('sm')]: {
              alignSelf: 'start',
              ...typography.h4,
            },
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: '0 16px 16px',
          },
          dividers: {
            padding: '16px',
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '16px',
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          contained: {
            marginLeft: 0,
            marginTop: 7,
            lineHeight: '120%',
          },
        },
      },
      MuiStepper: {
        styleOverrides: {
          root: {
            padding: 0,
            background: 'transparent',
            justifyContent: 'space-between',
            overflow: 'hidden',
          },
        },
      },
      MuiStep: {
        styleOverrides: {
          horizontal: {
            paddingLeft: 0,
            paddingRight: 0,
          },
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          label: {
            display: 'none',
          },
          iconContainer: {
            zIndex: 4,
          },
          vertical: {
            '& $label': {
              display: 'block',
            },
          },
        },
      },
      MuiStepIcon: {
        styleOverrides: {
          root: {
            width: '75px',
            height: '50px',
          },
        },
      },
      MuiStepConnector: {
        styleOverrides: {
          root: {
            [`&.${stepConnectorClasses.disabled}`]: {
              zIndex: 1,
            },
            [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
              backgroundColor: palette.secondary.main,
              zIndex: 2,
            },
            [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
              backgroundColor: palette.secondary.main,
            },
            [`&.${stepConnectorClasses.vertical} .${stepConnectorClasses.line}`]: {
              width: 1,
              height: 'auto',
            },
          },
          alternativeLabel: {
            top: '50%',
            left: '-50%',
            right: '50%',
          },
          line: {
            height: 1,
            border: 0,
            backgroundColor: palette.grey[400],
            borderRadius: 1,
            zIndex: 1,
          },
          lineHorizontal: {
            borderTopWidth: 0,
          },
        },
      },
      MuiSlider: {
        styleOverrides: {
          rail: {
            backgroundColor: palette.grey[400],
          },
          markLabel: {
            color: palette.grey[400],
            fontSize: '12px',
          },
          markLabelActive: {
            color: palette.grey[400],
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            ...typography.body1,
            color: palette.grey[700],
            borderBottom: 'none',
            padding: '8px',
            '&:first-of-type': {
              paddingLeft: '16px',
            },
            '&:last-of-type': {
              paddingRight: '16px',
            },
          },
          head: {
            ...typography.body2,
            color: palette.grey[600],
          },
          stickyHeader: {
            background: palette.common.white,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            height: '48px',
            '&.Mui-selected': {
              outline: `2px solid ${palette.common.black}`,
              outlineOffset: '-2px',
              backgroundColor: palette.common.white,
            },
            '&.Mui-selected&:nth-of-type(odd):not(:hover)': {
              backgroundColor: alpha(palette.grey[800], 0.04),
            },
            '&:not(.MuiTableRow-head):nth-of-type(odd)': {
              backgroundColor: alpha(palette.grey[800], 0.04),
            },
            '&.MuiTableRow-hover:hover': {
              cursor: 'pointer',
              backgroundColor: alpha(palette.grey[800], 0.1),
            },
          },
          head: {
            cursor: 'auto',
          },
        },
      },
      MuiTableBody: {
        styleOverrides: {
          root: {
            borderBottom: `none`,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontFamily: '"Delivery Light", Calibri, Arial, sans-serif',
            fontSize: '14px',
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: {
            padding: 0,
            '& .MuiDataGrid-main.MuiDataGrid-main': {
              border: 'none',
            },
          },
          content: {
            display: 'flex',
            alignItems: 'center',
            height: '64px',
            paddingLeft: '16px',
          },
          title: {
            ...typography.h4,
          },
          action: {
            marginTop: 0,
            marginRight: '16px',
            height: '64px',
            display: 'flex',
            alignItems: 'center',
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            '&:last-of-type': {
              paddingBottom: '16px',
            },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            ...typography.body2,
            border: `1px solid ${palette.grey[300]}`,
            textTransform: 'none',
            color: palette.grey[700],
            height: '36px',
            minWidth: '36px',
            whiteSpace: 'nowrap',
            [breakpoints.up('sm')]: {
              fontSize: '12.6px',
              height: '32px',
              minWidth: '32px',
            },
            '&.Mui-selected': {
              fontFamily: '"Delivery Bold", Calibri, Arial, sans-serif',
              fontWeight: 700,
              color: palette.primary.main,
              background: 'none',
            },
            '&.Mui-disabled': {
              color: palette.grey[400],
              borderColor: palette.grey[300],
            },
            ':not(:last-of-type)': {
              borderRight: '0',
            },
            ':not(:first-of-type)': {
              borderLeft: '0',
              '::before': {
                content: '""',
                width: '1px',
                background: palette.grey[300],
                position: 'absolute',
                top: '3px',
                bottom: '3px',
                left: '-1px',
              },
            },
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          dense: {
            paddingRight: '16px !important',
          },
        },
      },
      MuiBreadcrumbs: {
        styleOverrides: {
          ol: {
            flexWrap: 'nowrap',
          },
          li: {
            whiteSpace: 'nowrap',
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            cursor: 'pointer',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: shape.borderRadius,
            color: palette.grey[600],
            height: '24px',
            border: `1px solid ${palette.grey[300]}`,
            [`&.${chipClasses.colorPrimary} > .${chipClasses.icon}`]: {
              color: palette.grey[100],
            },
            [`&.${chipClasses.colorPrimary}.${chipClasses.outlined}`]: {
              color: palette.primary.main,
            },
            [`&.${chipClasses.colorPrimary}.${chipClasses.outlined} > .${chipClasses.icon}`]: {
              color: palette.primary.main,
            },
            [`&.${chipClasses.colorSuccess} > .${chipClasses.icon}`]: {
              color: palette.grey[100],
            },
            [`&.${chipClasses.colorSuccess}.${chipClasses.outlined}`]: {
              color: palette.success.main,
            },
            [`&.${chipClasses.colorSuccess}.${chipClasses.outlined} > .${chipClasses.icon}`]: {
              color: palette.success.main,
            },
            [`&.${chipClasses.colorInfo} > .${chipClasses.icon}`]: {
              color: palette.grey[100],
            },
            [`&.${chipClasses.colorInfo}.${chipClasses.outlined}`]: {
              color: palette.info.main,
            },
            [`&.${chipClasses.colorInfo}.${chipClasses.outlined} > .${chipClasses.icon}`]: {
              color: palette.info.main,
            },
            [`&.${chipClasses.colorError} > .${chipClasses.icon}`]: {
              color: palette.grey[100],
            },
            [`&.${chipClasses.colorError}.${chipClasses.outlined}`]: {
              color: palette.error.main,
            },
            [`&.${chipClasses.colorError}.${chipClasses.outlined} > .${chipClasses.icon}`]: {
              color: palette.error.main,
            },
            [`&.${chipClasses.colorWarning} > .${chipClasses.icon}`]: {
              color: palette.grey[100],
            },
            [`&.${chipClasses.colorWarning}.${chipClasses.outlined}`]: {
              color: palette.warning.main,
            },
            [`&.${chipClasses.colorWarning}.${chipClasses.outlined} > .${chipClasses.icon}`]: {
              color: palette.warning.main,
            },
          },
          label: {
            ...typography.caption,
            lineHeight: 1,
            paddingLeft: '8px',
            paddingRight: '8px',
          },
          avatar: {
            ...typography.caption,
            lineHeight: 1,
            borderRadius: 0,
            margin: 0,
            marginRight: '3px',
            borderRight: palette.grey[500],
            background: palette.grey[300],
          },
          icon: {
            marginLeft: '8px',
            marginRight: '-4px',
            fontSize: '0.8rem',
            color: palette.grey[600],
          },
          deleteIcon: {
            width: '1em',
            fontSize: '0.8rem',
          },
          colorPrimary: {
            color: palette.grey[100],
            border: `1px solid ${palette.primary.main}`,
          },
          colorSuccess: {
            color: palette.grey[100],
            border: `1px solid ${palette.success.main}`,
          },
          colorInfo: {
            color: palette.grey[100],
            border: `1px solid ${palette.info.main}`,
          },
          colorError: {
            color: palette.grey[100],
            border: `1px solid ${palette.error.main}`,
          },
          colorWarning: {
            color: palette.grey[100],
            border: `1px solid ${palette.warning.main}`,
          },
          sizeSmall: {
            height: '18px',
          },
          labelSmall: {
            paddingLeft: '4px',
            paddingRight: '4px',
          },
          deleteIconSmall: {
            height: '1em',
            width: '1em',
            marginLeft: '-2px',
          },
          iconSmall: {
            height: '1em',
            width: '1em',
            marginRight: '-2px',
            marginLeft: '4px',
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            width: 40,
            height: 22,
            padding: 0,
            margin: 8,
            [breakpoints.down('lg')]: {
              width: 48,
              height: 24,
            },
          },
          switchBase: {
            padding: 0,
            [`&.${switchClasses.checked}`]: {
              transform: 'translateX(20px)',
              [breakpoints.down('lg')]: {
                transform: 'translateX(24px)',
              },
              [`&.${switchClasses.colorPrimary} + .${switchClasses.track}`]: {
                backgroundColor: palette.success.main,
                border: `1px solid ${palette.success.main}`,
              },
              [`&.${switchClasses.colorSecondary} + .${switchClasses.track}`]: {
                backgroundColor: palette.secondary.main,
                border: `1px solid ${palette.secondary.main}`,
              },
              [`&.${switchClasses.colorSecondary} .${switchClasses.thumb}`]: {
                border: `1px solid ${palette.secondary.main}`,
              },
              [`& + .${switchClasses.track}`]: {
                opacity: 1,
              },
            },
            [`&.${switchClasses.disabled}`]: {
              [`&.${switchClasses.checked} + .${switchClasses.track}`]: {
                backgroundColor: `${palette.grey[400]} !important`,
                border: `1px solid ${palette.grey[400]}`,
                opacity: 1,
              },
              [`&.${switchClasses.checked} .${switchClasses.thumb}`]: {
                border: `1px solid ${palette.grey[400]}`,
              },
              [`&.${switchClasses.track}`]: {
                backgroundColor: `${palette.grey[200]} !important`,
                border: `1px solid ${palette.grey[400]}`,
              },
              [`&.${switchClasses.thumb}`]: {
                border: `1px solid ${palette.grey[400]}`,
              },
            },
          },
          thumb: {
            width: 20,
            height: 20,
            border: `1px solid ${palette.grey[500]}`,
            backgroundColor: palette.common.white,
            borderRadius: '50%',
            boxShadow: '1px 1px 5px rgba(0, 0, 0, 0.15);',
            [breakpoints.down('lg')]: {
              width: 24,
              height: 24,
            },
          },
          track: {
            height: 20,
            borderRadius: 18,
            border: `1px solid ${palette.grey[500]}`,
            backgroundColor: palette.grey[200],
            opacity: 1,
            [breakpoints.down('lg')]: {
              height: 24,
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: palette.grey[700],
          },
          colorPrimary: {
            color: palette.primary.main,
          },
          colorSecondary: {
            color: palette.secondary.main,
          },
          sizeSmall: {
            padding: '4px',
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            border: 0,
          },
        },
      },
      MuiCheckbox: {
        defaultProps: {
          checkedIcon: (
            <CheckboxChecked
              sx={{
                '> path:first-of-type': {
                  fill: palette.common.black,
                },
                '> path:last-of-type': {
                  fill: palette.common.white,
                },
              }}
            />
          ),
          icon: <CheckboxUnchecked />,
          indeterminateIcon: (
            <CheckboxIndeterminate
              sx={{
                '> path:first-of-type': {
                  fill: palette.common.black,
                },
                '> rect': {
                  fill: palette.common.white,
                },
              }}
            />
          ),
        },
        styleOverrides: {
          root: {
            // default is always black according to UX
            color: palette.grey[800],
            ':hover': {
              backgroundColor: 'rgba(26, 26, 26, 0.04)',
            },
            '&.MuiCheckbox-indeterminate': {
              color: palette.grey[800],
            },
            '&.Mui-checked': {
              color: palette.grey[800],
            },
            '&.Mui-disabled': {
              color: palette.grey[300],
            },
            '&.Mui-disabled&.Mui-checked': {
              color: palette.grey[400],
            },
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          toolbarContainer: {
            padding: 0,
            marginBottom: '24px',
          },
          root: {
            border: 'none',
            '.MuiDataGrid-cell.MuiDataGrid-cell--editing:focus-within, .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },

            // fix: https://github.com/mui/mui-x/issues/8168#issuecomment-1465784733
            '& .MuiDataGrid-main': {
              width: 0,
              minWidth: '100%',
              background: palette.mode === 'light' ? palette.common.white : '#1C1C1D',
              borderRadius: '3px',
              border: `1px solid ${palette.grey[300]}`,
            },
            '& .MuiDataGrid-row': {
              // paddingLeft: 6,
              '&:nth-of-type(odd):not(:hover):not(.Mui-selected)': {
                backgroundColor: alpha(palette.grey[800], 0.05),
              },
              '&:hover': {
                backgroundColor: alpha(palette.grey[800], 0.1),
              },
            },
            // Styles applied to the cell element.
            '& .MuiDataGrid-cell': {
              ...typography.body1,
              color: palette.grey[700],
              borderBottom: 'none',
              cursor: 'pointer',
              '&:focus': {
                outline: 'none',
              },
            },
            '& .MuiDataGrid-row-disabled .MuiDataGrid-cell': {
              color: palette.text.disabled,
            },
            // Styles applied to the column header element.
            '& .MuiDataGrid-columnHeader': {
              ...typography.body2,
              color: palette.grey[600],
              '&:focus': {
                outline: 'none',
              },
              '&:focus-within': {
                outline: 'none',
              },
              background: palette.mode === 'light' ? palette.common.white : '#1C1C1D',
            },
            '& .MuiDataGrid-columnHeaderWrapper': {
              paddingLeft: 6,
            },
            // Styles applied to the column header's title element.
            '& .MuiDataGrid-columnHeaderTitle:focus': {
              outline: 'none',
            },
            // Styles applied to the outer columns container element.
            '& .MuiDataGrid-columnsContainer': {
              borderBottom: 'none',
              justifyContent: 'center',
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            padding: '8px',
            '&.Mui-checked svg': {
              color: palette.grey[700],
            },
          },
        },
      },
    },
  })

  return components!
}

const darkModeComponents = (palette: Palette) => {
  const { components } = createTheme({
    components: {
      MuiButton: {
        styleOverrides: {
          containedPrimary: {
            color: palette.grey[800],
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderColor: palette.grey[100],
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          root: {
            color: palette.grey[600],
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: palette.grey[100],
            border: `1px solid ${palette.grey[100]}`,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '& fieldset': {
              borderColor: `${palette.grey[300]} !important`,
            },
            '&.Mui-error fieldset': {
              borderColor: `${palette.error.main} !important`,
            },
            '&.Mui-focused fieldset': {
              borderColor: `${palette.grey[300]} !important`,
            },
          },
        },
      },
    },
  })
  return components!
}

export const createComponents = (
  palette: Palette,
  hasResponsiveTypography: boolean,
  typography: Theme['typography'],
  options: Omit<DhlThemeOptions, 'device'>
): Components<Theme> => {
  const light = lightModeComponents(palette, hasResponsiveTypography, typography, options)

  if (palette.mode === 'light') {
    return light
  }
  return deepmerge(light, darkModeComponents(palette))
}
